/**
 * Admin — Security layout route (5 sub-tabs).
 *
 * Sub-tabs:
 *   /admin/security                — Authentication (password, MFA, SSO) — default
 *   /admin/security/session        — Session TTL, IP zones, rate limits
 *   /admin/security/privileged     — Break-glass, admin roles, impersonation
 *   /admin/security/keys           — JWT keys, KMS, TLS certificates
 *   /admin/security/threat         — Anomaly rules, incidents, block lists
 */

import { createFileRoute, Outlet, Link, useLocation } from '@tanstack/react-router';
import {
  KeyRound, Clock, UserCog, Shield, AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/security')({
  component: SecurityLayout,
});

const SUB_TABS = [
  { label: 'Authentication',    path: '/admin/security',            icon: KeyRound },
  { label: 'Session & Access',  path: '/admin/security/session',    icon: Clock },
  { label: 'Privileged Access', path: '/admin/security/privileged', icon: UserCog },
  { label: 'Keys & Secrets',    path: '/admin/security/keys',       icon: Shield },
  { label: 'Threat Response',   path: '/admin/security/threat',     icon: AlertTriangle },
];

function SecurityLayout() {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tab bar */}
      <div className="border-b bg-background px-6 shrink-0">
        <nav className="flex items-center gap-1 overflow-x-auto whitespace-nowrap">
          {SUB_TABS.map(t => {
            const Icon = t.icon;
            const isActive = location.pathname === t.path
              || (t.path !== '/admin/security' && location.pathname.startsWith(t.path));
            const isExact = location.pathname === '/admin/security' || location.pathname === '/admin/security/';
            const active = t.path === '/admin/security' ? isExact : isActive;
            return (
              <Link
                key={t.path}
                to={t.path as any}
                title={t.label}
                aria-label={t.label}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px shrink-0 whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{t.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
